// Blog Post Types and Data
// Add new blog posts here - they will automatically appear in the overview

import whatsappShopImage from "@/assets/blog/whatsapp-shop-integration.jpg";
import aiAgentKundenserviceImage from "@/assets/blog/ai-agent-kundenservice.jpg";
import whatsappMarketingImage from "@/assets/blog/whatsapp-marketing.jpg";
import { aiAgentPlattformenVergleichDe } from "@/data/blog/aiAgentPlattformenVergleich";
import { botfriendsAlternativeDe } from "@/data/blog/botfriendsAlternative";
import { chatbaseAlternativeDe } from "@/data/blog/chatbaseAlternative";
import { finIntercomAlternativeDe } from "@/data/blog/finIntercomAlternative";
import { kauzAiAlternativeDe } from "@/data/blog/kauzAiAlternative";
import { limeConnectAlternativeDe } from "@/data/blog/limeConnectAlternative";
import { meliboAlternativeDe } from "@/data/blog/meliboAlternative";
import { moinAiAlternativeDe } from "@/data/blog/moinAiAlternative";
import { parloaAlternativeDe } from "@/data/blog/parloaAlternative";
import { superchatAlternativeDe } from "@/data/blog/superchatAlternative";

export interface BlogAuthor {
  name: string;
  avatar?: string;
  role?: string;
}

export interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  coverImage: string;
  author: BlogAuthor;
  publishDate: string;
  readingTime: string;
  category: string;
  tags: string[];
  content: BlogSection[];
  /** Optional intro HTML rendered without a heading above the first section */
  intro?: string;
  /** Optional human-readable date shown instead of the formatted publishDate */
  displayDate?: string;
  hideCoverInArticle?: boolean;
  hideGlobalCTA?: boolean;
  seo?: { title: string; description: string; path: string; keywords: string };
  closingNote?: { before: string; linkText: string; to: string; after: string };
}

export interface BlogSection {
  id: string;
  title: string;
  /** Optional shorter label for the table of contents */
  tocTitle?: string;
  content: string;
  faqItems?: Array<{ question: string; answer: string }>;
  presentation?: "summary";
}

// Categories for filtering (per language)
export const blogCategoriesDe = [
  "Alle Themen",
  "Integrationen",
  "WhatsApp Business",
  "AI Agents",
  "E-Commerce",
  "Kundenservice",
  "Marketing",
] as const;

export const blogCategoriesEn = [
  "All Topics",
  "Integrations",
  "WhatsApp Business",
  "AI Agents",
  "E-Commerce",
  "Customer Service",
  "Marketing",
] as const;

export const getBlogCategories = (lang: string) =>
  lang === "en" ? blogCategoriesEn : blogCategoriesDe;

export type BlogCategory = (typeof blogCategoriesDe)[number] | (typeof blogCategoriesEn)[number];

// Blog Posts Data - German
const blogPostsDe: BlogPost[] = [
  superchatAlternativeDe,
  parloaAlternativeDe,
  moinAiAlternativeDe,
  meliboAlternativeDe,
  limeConnectAlternativeDe,
  kauzAiAlternativeDe,
  finIntercomAlternativeDe,
  chatbaseAlternativeDe,
  botfriendsAlternativeDe,
  aiAgentPlattformenVergleichDe,
  {
    slug: "whatsapp-shop-integration-big-green-egg",
    title: "Wie Big Green Egg Head mit WhatsApp & Shop-Integration 340% mehr Beratungen erzielt",
    excerpt: "Erfahre, wie der Schweizer BBQ-Spezialist mit Chathero's WhatsApp AI Agent und Shop-Integration den Kundenservice revolutioniert hat.",
    coverImage: whatsappShopImage,
    author: { name: "Martin Burst", role: "Co-Founder & CEO" },
    publishDate: "2025-01-15",
    readingTime: "5 Min.",
    category: "Integrationen",
    tags: ["WhatsApp", "E-Commerce", "Case Study"],
    content: [
      {
        id: "einfuehrung",
        title: "Die Herausforderung im Premium-BBQ-Markt",
        content: `
          <p>Big Green Egg Head ist der führende Schweizer Spezialist für Premium-BBQ-Produkte. Mit über 500 Produkten im Sortiment – von Keramikgrills bis zu spezialisierten Zubehörteilen – stand das Team vor einer besonderen Herausforderung: Wie berät man Kunden effektiv bei komplexen Produktkonfigurationen?</p>
          <p>Die Lösung kam durch die Integration von Chathero's WhatsApp AI Agent mit direkter Shop-Anbindung. Das Ergebnis: Ein intelligenter BBQ-Konfigurator, der Kunden rund um die Uhr berät.</p>
        `,
      },
      {
        id: "loesung",
        title: "Die WhatsApp & Shop-Integration",
        content: `
          <p>Die Integration verbindet mehrere Komponenten zu einem nahtlosen Erlebnis:</p>
          <ul>
            <li><strong>Produkt-Wissensbasis:</strong> Alle 500+ Produkte mit technischen Details, Kompatibilität und Verwendungszwecken</li>
            <li><strong>BBQ-Konfigurator:</strong> Geführte Beratung basierend auf Grilltyp, Erfahrungslevel und Kochvorlieben</li>
            <li><strong>Live-Bestandsabfrage:</strong> Echtzeit-Verfügbarkeit direkt aus dem Shop-System</li>
            <li><strong>Bestellstatus:</strong> Kunden können jederzeit ihren Lieferstatus abfragen</li>
          </ul>
        `,
      },
      {
        id: "ergebnisse",
        title: "Messbare Ergebnisse",
        content: `
          <p>Nach 6 Monaten zeigen die Zahlen beeindruckende Verbesserungen:</p>
          <ul>
            <li><strong>340% mehr Beratungsgespräche</strong> durch die 24/7-Verfügbarkeit</li>
            <li><strong>€127 höherer durchschnittlicher Warenkorb</strong> durch gezielte Upselling-Empfehlungen</li>
            <li><strong>67% weniger Retouren</strong> durch bessere Produktberatung</li>
            <li><strong>4.8/5 Kundenzufriedenheit</strong> im WhatsApp-Kanal</li>
          </ul>
        `,
      },
      {
        id: "implementation",
        title: "Technische Umsetzung",
        content: `
          <p>Die Implementierung erfolgte in drei Phasen:</p>
          <h4>Phase 1: Wissensbasis aufbauen</h4>
          <p>Alle Produktdaten, FAQs und Beratungsinhalte wurden in die Chathero Knowledge Base importiert. Die KI lernte Produktzusammenhänge und typische Kundenanfragen.</p>
          <h4>Phase 2: Shop-Integration</h4>
          <p>Über die REST-API wurde die Verbindung zum Shopify-Store hergestellt. Der Agent kann nun Preise, Verfügbarkeiten und Bestellungen in Echtzeit abfragen.</p>
          <h4>Phase 3: Konfigurator-Logik</h4>
          <p>Der BBQ-Konfigurator wurde als geführter Dialog implementiert, der Kunden durch Fragen zu Grilltyp, Größe und Zubehör führt.</p>
        `,
      },
      {
        id: "fazit",
        title: "Fazit und nächste Schritte",
        content: `
          <p>Die Kombination aus WhatsApp AI Agent und Shop-Integration hat sich als Game-Changer für Big Green Egg Head erwiesen. Kunden erhalten kompetente Beratung genau dann, wenn sie sie brauchen – auch außerhalb der Geschäftszeiten.</p>
          <p>Für 2025 plant das Team den Ausbau um:</p>
          <ul>
            <li>Rezept-Empfehlungen basierend auf gekauften Produkten</li>
            <li>Proaktive Nachverfolgung für Zubehör und Verbrauchsmaterial</li>
            <li>Integration von Grillkursen und Events</li>
          </ul>
        `,
      },
    ],
  },
  {
    slug: "ai-agent-kundenservice-automatisieren",
    title: "AI Agents im Kundenservice: So automatisierst du 80% aller Anfragen",
    excerpt: "Lerne, wie moderne AI Agents repetitive Kundenanfragen übernehmen und dein Team für komplexe Fälle freispielen.",
    coverImage: aiAgentKundenserviceImage,
    author: { name: "Roland Wagner", role: "Co-Founder & CTO" },
    publishDate: "2025-01-10",
    readingTime: "7 Min.",
    category: "AI Agents",
    tags: ["AI", "Kundenservice", "Automatisierung"],
    content: [
      {
        id: "intro",
        title: "Die Zukunft des Kundenservice",
        content: `
          <p>AI Agents revolutionieren den Kundenservice. Anstatt auf jede einzelne Anfrage manuell zu antworten, übernehmen intelligente Assistenten die Erstbearbeitung und lösen Standard-Anfragen vollautomatisch.</p>
        `,
      },
      {
        id: "use-cases",
        title: "Typische Einsatzszenarien",
        content: `
          <p>Die häufigsten automatisierbaren Anfragen sind:</p>
          <ul>
            <li>Bestellstatus und Lieferinformationen</li>
            <li>Produktverfügbarkeit und Preisanfragen</li>
            <li>Kontodaten und Rechnungen</li>
            <li>FAQ und allgemeine Informationen</li>
          </ul>
        `,
      },
      {
        id: "implementation",
        title: "Implementierung Schritt für Schritt",
        content: `
          <p>Eine erfolgreiche AI-Agent-Integration folgt diesen Phasen:</p>
          <ol>
            <li>Analyse der häufigsten Anfragen</li>
            <li>Aufbau der Wissensbasis</li>
            <li>Training und Testing</li>
            <li>Schrittweiser Rollout</li>
          </ol>
        `,
      },
    ],
  },
  {
    slug: "whatsapp-marketing-kampagnen",
    title: "WhatsApp Marketing: 5 Kampagnen-Typen mit über 90% Öffnungsrate",
    excerpt: "Entdecke die effektivsten WhatsApp-Marketing-Strategien für E-Commerce und Dienstleister.",
    coverImage: whatsappMarketingImage,
    author: { name: "Martin Burst", role: "Co-Founder & CEO" },
    publishDate: "2025-01-05",
    readingTime: "6 Min.",
    category: "Marketing",
    tags: ["WhatsApp", "Marketing", "Kampagnen"],
    content: [
      {
        id: "intro",
        title: "Warum WhatsApp Marketing?",
        content: `
          <p>Mit Öffnungsraten von über 90% übertrifft WhatsApp jeden anderen Marketing-Kanal deutlich. Die persönliche, direkte Kommunikation schafft Vertrauen und führt zu höheren Conversion-Raten.</p>
        `,
      },
      {
        id: "kampagnen",
        title: "Die 5 effektivsten Kampagnen-Typen",
        content: `
          <ul>
            <li><strong>Willkommens-Sequenzen:</strong> Neue Abonnenten optimal einführen</li>
            <li><strong>Abandoned Cart Recovery:</strong> Warenkorbabbrecher zurückgewinnen</li>
            <li><strong>Produktlaunches:</strong> Exklusive Vorankündigungen für VIP-Kunden</li>
            <li><strong>Re-Engagement:</strong> Inaktive Kunden reaktivieren</li>
            <li><strong>Post-Purchase:</strong> Bewertungen und Upselling</li>
          </ul>
        `,
      },
    ],
  },
];

// Blog Posts Data - English
const blogPostsEn: BlogPost[] = [
  {
    slug: "whatsapp-shop-integration-big-green-egg",
    title: "How Big Green Egg Head Achieved 340% More Consultations with WhatsApp & Shop Integration",
    excerpt: "Learn how the Swiss BBQ specialist revolutionized customer service with Chathero's WhatsApp AI Agent and shop integration.",
    coverImage: whatsappShopImage,
    author: { name: "Martin Burst", role: "Co-Founder & CEO" },
    publishDate: "2025-01-15",
    readingTime: "5 min",
    category: "Integrations",
    tags: ["WhatsApp", "E-Commerce", "Case Study"],
    content: [
      {
        id: "introduction",
        title: "The Challenge in the Premium BBQ Market",
        content: `
          <p>Big Green Egg Head is the leading Swiss specialist for premium BBQ products. With over 500 products in their range – from ceramic grills to specialized accessories – the team faced a particular challenge: How do you effectively advise customers on complex product configurations?</p>
          <p>The solution came through the integration of Chathero's WhatsApp AI Agent with direct shop connectivity. The result: An intelligent BBQ configurator that advises customers around the clock.</p>
        `,
      },
      {
        id: "solution",
        title: "The WhatsApp & Shop Integration",
        content: `
          <p>The integration connects multiple components into a seamless experience:</p>
          <ul>
            <li><strong>Product Knowledge Base:</strong> All 500+ products with technical details, compatibility, and use cases</li>
            <li><strong>BBQ Configurator:</strong> Guided consultation based on grill type, experience level, and cooking preferences</li>
            <li><strong>Live Stock Queries:</strong> Real-time availability directly from the shop system</li>
            <li><strong>Order Status:</strong> Customers can check their delivery status at any time</li>
          </ul>
        `,
      },
      {
        id: "results",
        title: "Measurable Results",
        content: `
          <p>After 6 months, the numbers show impressive improvements:</p>
          <ul>
            <li><strong>340% more consultation sessions</strong> through 24/7 availability</li>
            <li><strong>€127 higher average cart value</strong> through targeted upselling recommendations</li>
            <li><strong>67% fewer returns</strong> through better product consultation</li>
            <li><strong>4.8/5 customer satisfaction</strong> on the WhatsApp channel</li>
          </ul>
        `,
      },
      {
        id: "implementation",
        title: "Technical Implementation",
        content: `
          <p>The implementation was carried out in three phases:</p>
          <h4>Phase 1: Building the Knowledge Base</h4>
          <p>All product data, FAQs, and consultation content were imported into the Chathero Knowledge Base. The AI learned product relationships and typical customer inquiries.</p>
          <h4>Phase 2: Shop Integration</h4>
          <p>The connection to the Shopify store was established via REST API. The agent can now query prices, availability, and orders in real time.</p>
          <h4>Phase 3: Configurator Logic</h4>
          <p>The BBQ configurator was implemented as a guided dialogue that takes customers through questions about grill type, size, and accessories.</p>
        `,
      },
      {
        id: "conclusion",
        title: "Conclusion and Next Steps",
        content: `
          <p>The combination of WhatsApp AI Agent and shop integration has proven to be a game-changer for Big Green Egg Head. Customers receive expert advice exactly when they need it – even outside business hours.</p>
          <p>For 2025, the team plans to expand with:</p>
          <ul>
            <li>Recipe recommendations based on purchased products</li>
            <li>Proactive follow-ups for accessories and consumables</li>
            <li>Integration of BBQ courses and events</li>
          </ul>
        `,
      },
    ],
  },
  {
    slug: "ai-agent-kundenservice-automatisieren",
    title: "AI Agents in Customer Service: How to Automate 80% of All Inquiries",
    excerpt: "Learn how modern AI Agents handle repetitive customer inquiries and free your team for complex cases.",
    coverImage: aiAgentKundenserviceImage,
    author: { name: "Roland Wagner", role: "Co-Founder & CTO" },
    publishDate: "2025-01-10",
    readingTime: "7 min",
    category: "AI Agents",
    tags: ["AI", "Customer Service", "Automation"],
    content: [
      {
        id: "intro",
        title: "The Future of Customer Service",
        content: `
          <p>AI Agents are revolutionizing customer service. Instead of manually responding to every single inquiry, intelligent assistants handle initial processing and resolve standard requests fully automatically.</p>
        `,
      },
      {
        id: "use-cases",
        title: "Typical Use Cases",
        content: `
          <p>The most common automatable inquiries are:</p>
          <ul>
            <li>Order status and delivery information</li>
            <li>Product availability and pricing inquiries</li>
            <li>Account data and invoices</li>
            <li>FAQs and general information</li>
          </ul>
        `,
      },
      {
        id: "implementation",
        title: "Step-by-Step Implementation",
        content: `
          <p>A successful AI Agent integration follows these phases:</p>
          <ol>
            <li>Analysis of the most frequent inquiries</li>
            <li>Building the knowledge base</li>
            <li>Training and testing</li>
            <li>Gradual rollout</li>
          </ol>
        `,
      },
    ],
  },
  {
    slug: "whatsapp-marketing-kampagnen",
    title: "WhatsApp Marketing: 5 Campaign Types with Over 90% Open Rate",
    excerpt: "Discover the most effective WhatsApp marketing strategies for e-commerce and service providers.",
    coverImage: whatsappMarketingImage,
    author: { name: "Martin Burst", role: "Co-Founder & CEO" },
    publishDate: "2025-01-05",
    readingTime: "6 min",
    category: "Marketing",
    tags: ["WhatsApp", "Marketing", "Campaigns"],
    content: [
      {
        id: "intro",
        title: "Why WhatsApp Marketing?",
        content: `
          <p>With open rates of over 90%, WhatsApp significantly outperforms every other marketing channel. The personal, direct communication builds trust and leads to higher conversion rates.</p>
        `,
      },
      {
        id: "campaigns",
        title: "The 5 Most Effective Campaign Types",
        content: `
          <ul>
            <li><strong>Welcome Sequences:</strong> Optimally onboard new subscribers</li>
            <li><strong>Abandoned Cart Recovery:</strong> Win back cart abandoners</li>
            <li><strong>Product Launches:</strong> Exclusive pre-announcements for VIP customers</li>
            <li><strong>Re-Engagement:</strong> Reactivate inactive customers</li>
            <li><strong>Post-Purchase:</strong> Reviews and upselling</li>
          </ul>
        `,
      },
    ],
  },
];

// Get blog posts for current language
export const getBlogPosts = (lang: string): BlogPost[] =>
  lang === "en" ? blogPostsEn : blogPostsDe;

// Legacy export for backward compatibility
export const blogPosts = blogPostsDe;
export const blogCategories = blogCategoriesDe;

// Helper function to get a blog post by slug
export const getBlogPostBySlug = (slug: string, lang?: string): BlogPost | undefined => {
  const posts = lang ? getBlogPosts(lang) : blogPostsDe;
  return posts.find((post) => post.slug === slug);
};

// Helper function to get posts by category
export const getBlogPostsByCategory = (category: string, lang?: string): BlogPost[] => {
  const posts = lang ? getBlogPosts(lang) : blogPostsDe;
  const allLabel = lang === "en" ? "All Topics" : "Alle Themen";
  if (category === allLabel) return posts;
  return posts.filter((post) => post.category === category);
};

// Helper function to get related posts
export const getRelatedPosts = (currentSlug: string, limit: number = 3, lang?: string): BlogPost[] => {
  const posts = lang ? getBlogPosts(lang) : blogPostsDe;
  const currentPost = posts.find((p) => p.slug === currentSlug);
  if (!currentPost) return posts.slice(0, limit);

  const otherPosts = posts.filter((post) => post.slug !== currentSlug);

  const scoredPosts = otherPosts.map((post) => {
    let score = 0;
    if (post.category === currentPost.category) score += 2;
    post.tags.forEach((tag) => {
      if (currentPost.tags.includes(tag)) score += 1;
    });
    return { post, score };
  });

  scoredPosts.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return new Date(b.post.publishDate).getTime() - new Date(a.post.publishDate).getTime();
  });

  return scoredPosts.slice(0, limit).map((sp) => sp.post);
};

// Helper function to format date
export const formatBlogDate = (dateString: string, lang?: string): string => {
  const date = new Date(dateString);
  const locale = lang === "en" ? "en-US" : "de-DE";
  return date.toLocaleDateString(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};
